<template>
  <div class="page">
    <!-- 1) Mapa -->
    <div class="map-container">
      <div class="map-wrapper">
        <l-map :zoom="zoomMapa" :center="mapCenter" :use-global-leaflet="false" ref="lmap"
          :options="{ attributionControl: false, minZoom: minZoom, maxZoom: maxZoom, maxBounds: spainBounds, maxBoundsViscosity: 1.0 }">
          <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" layer-type="base" name="OpenStreetMap">
          </l-tile-layer>
          <!-- Los marcadores se agregan programáticamente al mapa -->
        </l-map>
        <!-- Botón de filtros -->
        <button class="title-bar-button map-filters-button"
          :aria-label="$t('components.chargerresultslistview.filtros')"
          :title="$t('components.chargerresultslistview.filtros')" @click="openFilters">
          <i class="fa-solid fa-sliders-up"></i>
          <span v-if="hasActiveFilters" class="filter-indicator"></span>
        </button>
        <!-- Botón para borrar marcadores -->
        <button class="title-bar-button map-clear-markers-button" 
          v-show="false"
          :aria-label="$t('components.chargerresultsmapview.borrar_marcadores')"
          :title="$t('components.chargerresultsmapview.borrar_marcadores')" @click="clearMapMarkers">
          <i class="fa-solid fa-trash"></i>
        </button>
        <!-- Botón de ubicación personalizado -->
        <button 
          class="title-bar-button map-location-button"
          :class="{ 
            'active': isLocateFollowing, 
            'locating': isLocating,
            'error': locationError || locationErrorFromButton 
          }"
          :aria-label="$t('components.chargerresultsmapview.centrar_ubicacion')"
          :title="$t('components.chargerresultsmapview.centrar_ubicacion')" 
          @click="handleLocationButtonClick">
          <i v-if="isLocating" class="fa-solid fa-spinner fa-spin"></i>
          <i v-else-if="isLocateFollowing" class="fa-solid fa-crosshairs"></i>
          <i v-else class="fa-solid fa-location-crosshairs"></i>
        </button>
      </div>
    </div>

    <!-- Mensaje si no hay acceso a la ubicación (mensaje superior original) -->
    <div v-if="locationError" class="message-container">
      <div class="location-icon"><img src="@assets/images/location-dot.svg" alt="Location" /></div>
      <div class="message-text">
        {{ locationError }}
        <a href="#" @click.prevent="showLocationHelp = true" class="help-link">{{
          $t("components.chargersetupview.ayuda") }}</a>
      </div>
    </div>

    <!-- Panel flotante con ChargerListItem -->
    <div v-if="selectedPoint" class="floating-panel">
      <button class="close-button" @click="closeModal">✕</button>
      <ChargerListItem :name="selectedPointData.name" :distanceKm="selectedPointData.distanceKm"
        :priceKwh="selectedPointData.priceKwh" :totalCost="selectedPointData.totalCost"
        :powerKw="selectedPointData.powerKw" :chargeTimeMin="selectedPointData.chargeTimeMin"
        :latitud="selectedPointData.latitud" :longitud="selectedPointData.longitud" :Libre="selectedPointData.libre"
        :Total="selectedPointData.total" :groupedCount="selectedChargerGroup.length" :currentIndex="currentChargerIndex"
        @navigate-next="navigateToNext" @navigate-previous="navigateToPrevious"
        @navigation-error="handleNavigationError" />
    </div>

    <!-- Ventana flotante de error de geolocalización (del control de locate) -->
    <div v-if="locationErrorFromButton" class="floating-error-panel">
      <button class="close-button" @click="locationErrorFromButton = null">✕</button>
      <div class="error-content">
        <div class="error-icon">
          <i class="fa-solid fa-triangle-exclamation"></i>
        </div>
        <div class="error-text">
          <div class="error-title">{{ $t("components.chargerresultsmapview.error_geolocalizacion") }}</div>
          <div class="error-message">{{ locationErrorFromButton }}</div>
          <a href="#" @click.prevent="showLocationHelp = true" class="help-link">{{
            $t("components.chargersetupview.ayuda") }}</a>
        </div>
      </div>
    </div>

    <!-- Ventana flotante de error de navegación (cuando se intenta abrir mapas) -->
    <div v-if="navigationError" class="floating-error-panel">
      <button class="close-button" @click="navigationError = null">✕</button>
      <div class="error-content">
        <div class="error-icon">
          <i class="fa-solid fa-triangle-exclamation"></i>
        </div>
        <div class="error-text">
          <div class="error-title">{{ $t("components.chargerresultsmapview.error_navegacion") }}</div>
          <div class="error-message">{{ navigationError }}</div>
        </div>
      </div>
    </div>

    <!-- 2) Footer -->
    <ChargerResultsFooter activeTab="map" />

    <!-- Modal de Filtros -->
    <ChargerListItemFilter v-if="showFilters" :activeFilters="activeFilters" :hideDistance="true" @close="showFilters = false"
      @apply="handleApplyFilters" />

    <!-- Modal LocationHelpPicker -->
    <LocationHelpPicker v-if="showLocationHelp" @close="showLocationHelp = false" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, nextTick, watch, onActivated } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from 'vue-i18n';
import ChargerListItem from "@/components/charger/ChargerListItem.vue";
import ChargerResultsFooter from "@/components/share/ChargerResultsFooter.vue";
import ChargerListItemFilter from "@/components/charger/ChargerListItemFilter.vue";
import LocationHelpPicker from "@/components/setup/LocationHelpPicker.vue";
import { LMap, LTileLayer } from '@vue-leaflet/vue-leaflet';
import type { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import { useApi } from "@api/integration"
const route = useRoute();
const api = useApi();
const { t } = useI18n();

// Configuración del mapa
const zoomMapa = 14;
const latitudMadrid = 40.41694;
const longitudMadrid = -3.70361;
const lmap = ref<typeof LMap>();
const markerGroup = ref<L.LayerGroup | null>(null); // Capa única para todos los marcadores
const userLocationMarker = ref<L.Marker | null>(null); // Marcador de ubicación del usuario
const userLocationCircle = ref<L.Circle | null>(null); // Círculo de precisión de ubicación
const isLocateFollowing = ref<boolean>(false); // Estado de seguimiento del control de ubicación
const userClickedLocateButton = ref<boolean>(false); // Rastrear si el usuario hizo clic en el botón de ubicación
const isLocating = ref<boolean>(false); // Estado de búsqueda de ubicación
const locationWatchId = ref<number | null>(null); // ID del watchPosition para poder detenerlo
let globalErrorHandler: ((event: ErrorEvent) => void) | null = null; // Manejador global de errores para _latLngToNewLayerPoint
let globalRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null; // Manejador global para promesas rechazadas

// Límites de zoom
const minZoom = 10;  // Alejamiento máximo permitido (número menor = más alejado)
const maxZoom = 18; // Acercamiento máximo permitido (número mayor = más cerca)

// Límites geográficos para España, Baleares e Islas Canarias (con margen adicional)
// [sur, oeste], [norte, este]
const spainBounds: L.LatLngBoundsExpression = [
  [26.5, -19.0],  // Sur-Oeste (sur de Canarias con margen)
  [45.0, 6.0]      // Norte-Este (norte de España, este de Baleares con margen)
];

// Estado del modal
const selectedPoint = ref<string | null>(null);

// Datos de búsqueda desde localStorage
interface SearchData {
  vehicle: string;
  IdCocheElectrico: string;
  IdTarifaNombre: number | null;
  batteryCurrent: number;
  batteryTarget: number;
  BateriaInicial: number;
  BateriaFinal: number;
  location: { lat: number; lon: number } | null;
}

const searchData = ref<SearchData | null>(null);

// Tipo para los cargadores
type ChargerItem = {
  id: string;
  name: string;
  distanceKm: number;
  priceKwh: number;
  totalCost: number;
  powerKw: number;
  chargeTimeMin: number;
  latitud: number;
  longitud: number;
  libre: number;
  total: number;
};

const chargers = ref<ChargerItem[]>([]);

// IDs de cargadores ya pintados en el mapa (para evitar duplicados)
const paintedChargerIds = ref<Set<string>>(new Set());

// Variable para controlar si estamos en modo de actualización incremental (true) o reemplazo completo (false)
const isIncrementalUpdate = ref(false);

// Flag para evitar recargar marcadores después de limpiarlos manualmente
const isClearingMarkers = ref(false);

// Flag para controlar la primera carga inicial
const isInitialLoad = ref(true);

// Modal de filtros
const showFilters = ref(false);

// Modal de ayuda de ubicación
const showLocationHelp = ref(false);

// Filtros activos
interface ActiveFilters {
  chargerStatus: string;
  priceMax: number;
  distance: number;
  chargingTime: string;
}

const activeFilters = ref<ActiveFilters | null>(null);

// Computed para verificar si hay filtros activos
const hasActiveFilters = computed(() => {
  if (!activeFilters.value) return false;
  
  const filters = activeFilters.value;
  return (
    filters.chargerStatus === 'libre' ||
    filters.chargingTime !== 'any' ||
    (filters.priceMax > 0 && filters.priceMax !== 1) ||
    (filters.distance > 0 && filters.distance !== 10)
  );
});

// Ubicación del usuario
const userLocation = ref<{ lat: number; lon: number } | null>(null);
const userHeading = ref<number | null>(null); // Orientación del usuario en grados (0-360)
const deviceAlpha = ref<number | null>(null); // Valor alpha/webkitCompassHeading del DeviceOrientationEvent
const isIOS = ref<boolean>(false); // Indica si estamos usando iOS (webkitCompassHeading)
const locationError = ref<string | null>(null); // Error inicial al cargar la página (mensaje superior)
const locationErrorFromButton = ref<string | null>(null); // Error del control de geolocalización (ventana flotante)
const navigationError = ref<string | null>(null); // Error al intentar abrir mapas


// Radio diagonal del mapa visible (en metros)
const mapVisibleRadius = ref<number>(10000); // Valor por defecto: 10 km

// Centro anterior del mapa (para detectar cambios)
const previousMapCenter = ref<{ lat: number; lng: number } | null>(null);

// Función para obtener o crear la capa única de marcadores
function getOrCreateMarkerLayer(): L.LayerGroup {
  const map = lmap.value?.leafletObject;
  if (!map) {
    throw new Error('El mapa no está disponible');
  }

  // Si ya existe la capa y está en el mapa, devolverla
  if (markerGroup.value && (markerGroup.value as any)._map) {
    return markerGroup.value as L.LayerGroup;
  }

  // Si existe pero no está en el mapa, añadirla
  if (markerGroup.value) {
    markerGroup.value.addTo(map);
    return markerGroup.value as L.LayerGroup;
  }

  // Si no existe, crearla y añadirla al mapa
  markerGroup.value = L.layerGroup();
  markerGroup.value.addTo(map);
  return markerGroup.value as L.LayerGroup;
}


// Función para crear iconos de cargador según su estado Libre
function getChargerIcon(libre: number, groupedCount: number = 1): L.DivIcon {
  const color = libre > 0 ? '#26bf64' : '#cb1212'; // Verde si hay tomas libres, rojo si no

  // Ajustar tamaño según si hay agrupación
  const size = groupedCount > 1 ? 28 : 20;
  const fontSize = groupedCount > 1 ? '12px' : '0px';
  const displayNumber = groupedCount > 1 ? groupedCount : '';

  return L.divIcon({
    className: 'charger-marker',
    html: `<div style="background-color: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: ${fontSize}; line-height: 1;">${displayNumber}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
}


// Centro del mapa basado en la ubicación del usuario o Madrid por defecto
const mapCenter = computed<LatLngExpression>(() => {
  if (searchData.value?.location) {
    return [searchData.value.location.lat, searchData.value.location.lon];
  }
  return [latitudMadrid, longitudMadrid];
});

const selectedPointData = ref({
  name: "",
  distanceKm: 0,
  priceKwh: 0,
  totalCost: 0,
  powerKw: 0,
  chargeTimeMin: 0,
  latitud: 0,
  longitud: 0,
  libre: 0,
  total: 0
});

// Grupo de cargadores seleccionado e índice actual
const selectedChargerGroup = ref<ChargerItem[]>([]);
const currentChargerIndex = ref<number>(0);

function formatHoraCliente(d: Date) {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Función helper para formatear números con coma como separador decimal
function formatNumberWithComma(value: number, decimals: number): string {
  return value.toFixed(decimals).replace('.', ',');
}

// Cargar datos de búsqueda y obtener resultados
async function loadSearchResults() {
  try {
    // Leer datos de búsqueda
    const storedData = localStorage.getItem('chargerSearchData');
    if (!storedData) {
      console.warn('No hay datos de búsqueda almacenados');
      return;
    }

    searchData.value = JSON.parse(storedData);

    // Si hay ubicación, buscar cargadores cercanos
    if (searchData.value?.location) {
      // Validar parámetros requeridos
      if (!searchData.value.location?.lat || !searchData.value.location?.lon) {
        throw new Error(t("components.chargerresultsmapview.ubicacion_requerida"));
      }

      if (!searchData.value.IdCocheElectrico) {
        throw new Error(t("components.chargerresultsmapview.vehiculo_requerido"));
      }

      // Si es actualización incremental, usar el centro del mapa visible
      let searchLat = searchData.value.location.lat;
      let searchLon = searchData.value.location.lon;

      if (isIncrementalUpdate.value) {
        const map = lmap.value?.leafletObject;
        if (map) {
          const mapCenter = map.getCenter();
          searchLat = mapCenter.lat;
          searchLon = mapCenter.lng;
        }
      }

      // Buscar cargadores con filtro por ubicación
      // Nota: Los nombres de parámetros deben coincidir exactamente con el swagger (PascalCase)
      const now = new Date();
      const queryParams: Record<string, any> = {
        Latitud: searchLat,
        Longitud: searchLon,
        Take: 200,
        Skip: 0,
        OrderBy: "Distancia", // Por defecto: Distancia (según CargadorOrderByEnum), aunque en la vista mapa es relevante
        BateriaInicial: searchData.value.BateriaInicial ?? searchData.value.batteryCurrent,
        BateriaFinal: searchData.value.BateriaFinal ?? searchData.value.batteryTarget,
        IdCocheElectrico: searchData.value.IdCocheElectrico,
        HoraCliente: formatHoraCliente(now),
      };

      // Solo agregar IdTarifaNombre si tiene un valor válido (no null ni undefined)
      if (searchData.value.IdTarifaNombre != null) {
        queryParams.IdTarifaNombre = searchData.value.IdTarifaNombre;
      }

      // Aplicar filtros si están activos
      if (activeFilters.value) {
        // Precio máximo (según swagger: PrecioHasta)
        if (activeFilters.value.priceMax > 0) {
          queryParams.PrecioHasta = activeFilters.value.priceMax;
        }

        // Estado del cargador: SoloLibres = true si está seleccionado "Libre"
        // Si selecciona "Todos" no hacemos nada (no pasamos el parámetro)
        if (activeFilters.value.chargerStatus === 'libre') {
          queryParams.SoloLibres = true;
        }

        // Tiempo de carga máximo (según swagger: TiempoMaximo, en minutos)
        if (activeFilters.value.chargingTime !== 'any') {
          const timeMap: Record<string, number> = {
            '<15': 15,
            '<30': 30,
            '<45': 45,
            '<60': 60
          };
          const maxTimeMinutes = timeMap[activeFilters.value.chargingTime];
          if (maxTimeMinutes) {
            queryParams.TiempoMaximo = maxTimeMinutes;
          }
        }

        // Distancia máxima (según swagger: RadioMetros, en metros)
        // Convertimos de km (valor del filtro) a metros
        if (activeFilters.value.distance > 0) {
          queryParams.RadioMetros = activeFilters.value.distance * 1000; 
        }
      }

      // El radio en el mapa, siempre el visible
      queryParams.RadioMetros = Math.round(mapVisibleRadius.value);
      
      // Filtrar parámetros null/undefined antes de enviar
      const cleanParams = Object.fromEntries(
        Object.entries(queryParams).filter(([_, value]) => value != null && value !== undefined && value !== '')
      );


      // Asegurar que OrderBy siempre tenga un valor válido (es obligatorio)
      const orderByValue = queryParams.OrderBy || "Distancia";

      const cargadores = await api.callStandardApi(
        "getCargadorAll",
        { showLoading: false },
        queryParams["Take"],
        queryParams["Skip"],
        queryParams["BateriaInicial"],
        queryParams["BateriaFinal"],
        queryParams["IdCocheElectrico"],
        queryParams["IdTarifaNombre"],
        queryParams["Latitud"],
        queryParams["Longitud"],
        queryParams["HoraCliente"],
        orderByValue, // OrderBy es requerido
        queryParams["RadioMetros"],
        queryParams["SoloLibres"],
        queryParams["PrecioHasta"],
        queryParams["TiempoMaximo"]
      );


      // Transformar datos de la API al formato esperado
      let nuevosCargadores = cargadores
        .filter((item: any) => {
          // Validar que las coordenadas existen y son válidas
          const lat = Number(item.Latitud);
          const lon = Number(item.Longitud);
          return (
            item.Latitud != null &&
            item.Longitud != null &&
            !isNaN(lat) &&
            !isNaN(lon) &&
            lat >= -90 && lat <= 90 &&
            lon >= -180 && lon <= 180
          );
        })
        .map((item: any) => {
          // Convertir coordenadas a números para asegurar que sean válidas
          const lat = Number(item.Latitud);
          const lon = Number(item.Longitud);

          return {
            id: item.Id,
            name: item.Direccion,
            distanceKm: item.DistanciaM ? Math.round(item.DistanciaM / 1000 * 10) / 10 : 0,
            priceKwh: item.PrecioKwh || 0,
            totalCost: item.CosteTotal || 0,
            powerKw: item.Potencia || 0,
            chargeTimeMin: item.TiempoCarga || 0,
            latitud: lat, // Ya convertido a número
            longitud: lon, // Ya convertido a número
            libre: item.Libre ?? 0,
            total: item.Total ?? 0
          };
        });

      // Aplicar filtros del lado del cliente (para filtros que no soporta la API o como respaldo)
      if (activeFilters.value) {
        nuevosCargadores = nuevosCargadores.filter((item: ChargerItem) => {
          // Filtro por precio máximo
          if (activeFilters.value!.priceMax > 0 && item.priceKwh > activeFilters.value!.priceMax) {
            return false;
          }


          // Filtro por tiempo de carga
          if (activeFilters.value!.chargingTime !== 'any') {
            const timeMap: Record<string, number> = {
              '<15': 15,
              '<30': 30,
              '<45': 45,
              '<60': 60
            };
            const maxTimeMinutes = timeMap[activeFilters.value!.chargingTime];
            if (maxTimeMinutes && item.chargeTimeMin > maxTimeMinutes) {
              return false;
            }
          }

          // Filtro por estado del cargador
          if (activeFilters.value!.chargerStatus === 'libre' && item.libre === 0) {
            return false;
          }

          return true;
        });
      }

      // Si es actualización incremental, fusionar con los existentes (solo nuevos)
      if (isIncrementalUpdate.value) {
        const existingIds = new Set(chargers.value.map((c: ChargerItem) => c.id));
        const newChargers = nuevosCargadores.filter((c: ChargerItem) => !existingIds.has(c.id));

        if (newChargers.length > 0) {
          chargers.value = [...chargers.value, ...newChargers];
        } else {
        }
      } else {
        // Reemplazo completo (primera carga o aplicación de filtros)
        chargers.value = nuevosCargadores;
        paintedChargerIds.value.clear(); // Limpiar IDs pintados
      }

      // Centrar el mapa en la ubicación del usuario solo si:
      // 1. No es actualización incremental
      // 2. El usuario hizo clic explícitamente en el botón (NO centrar automáticamente)
      // 3. Hay cargadores y hay ubicación
      if (!isIncrementalUpdate.value && chargers.value.length > 0 && searchData.value.location) {
        // SOLO centrar cuando el usuario hace clic explícitamente en el botón
        if (userClickedLocateButton.value) {
          const map = lmap.value?.leafletObject;
          if (map) {
            map.setView([searchData.value.location.lat, searchData.value.location.lon], zoomMapa);
          }
        }
      }

      // Actualizar los marcadores en el mapa
      await nextTick();
      createOrUpdateMarkers();

    } else {
      console.warn('No hay ubicación disponible para buscar cargadores');
    }

  } catch (error: any) {
    console.error('Error cargando resultados:', error);

  // El error se maneja silenciosamente en la vista de mapa, pero lo logueamos para depuración
    if (error?.response?.status === 400) {
      const errorMessage = error?.response?.data?.message || error?.response?.data || error?.message || 'Error en la solicitud';
      console.error('Detalles del error 400:', {
        url: error?.config?.url,
        params: error?.config?.params,
        response: error?.response?.data
      });
    } else if (error?.response?.status === 401) {
      console.error('Error 401: No autorizado');
    } else if (error?.response?.status === 500) {
      console.error('Error 500: Error del servidor');
    }
    
  }
}

// Tipo para grupos de cargadores
type ChargerGroup = {
  chargers: ChargerItem[];
  centerLat: number;
  centerLng: number;
  hasLibre: boolean; // true si alguno tiene libre > 0
  count: number;
};

// Función para agrupar cargadores cercanos (radio de 10 metros)
function groupNearbyChargers(chargersList: ChargerItem[], radiusMeters: number = 10): ChargerGroup[] {
  const groups: ChargerGroup[] = [];
  const processed = new Set<string>();

  for (const charger of chargersList) {
    // Si ya está procesado, saltarlo
    if (processed.has(charger.id)) {
      continue;
    }

    // Validar coordenadas
    const lat = Number(charger.latitud);
    const lon = Number(charger.longitud);
    if (
      charger.latitud == null ||
      charger.longitud == null ||
      isNaN(lat) ||
      isNaN(lon) ||
      lat < -90 || lat > 90 ||
      lon < -180 || lon > 180
    ) {
      continue;
    }

    const chargerLatLng = L.latLng(lat, lon);
    const group: ChargerItem[] = [charger];
    processed.add(charger.id);

    // Buscar otros cargadores dentro del radio
    for (const otherCharger of chargersList) {
      if (processed.has(otherCharger.id)) {
        continue;
      }

      const otherLat = Number(otherCharger.latitud);
      const otherLon = Number(otherCharger.longitud);
      if (
        otherCharger.latitud == null ||
        otherCharger.longitud == null ||
        isNaN(otherLat) ||
        isNaN(otherLon) ||
        otherLat < -90 || otherLat > 90 ||
        otherLon < -180 || otherLon > 180
      ) {
        continue;
      }

      const otherLatLng = L.latLng(otherLat, otherLon);
      const distance = chargerLatLng.distanceTo(otherLatLng);

      if (distance <= radiusMeters) {
        group.push(otherCharger);
        processed.add(otherCharger.id);
      }
    }

    // Calcular el centro del grupo (promedio de coordenadas)
    const avgLat = group.reduce((sum, c) => sum + Number(c.latitud), 0) / group.length;
    const avgLng = group.reduce((sum, c) => sum + Number(c.longitud), 0) / group.length;

    // Determinar si hay algún cargador libre
    const hasLibre = group.some(c => c.libre > 0);

    groups.push({
      chargers: group,
      centerLat: avgLat,
      centerLng: avgLng,
      hasLibre: hasLibre,
      count: group.length
    });
  }

  return groups;
  
}

// Limpiar todos los marcadores del mapa
function clearMapMarkers() {
  const map = lmap.value?.leafletObject;
  
  if (!map) {
    // Si no hay mapa, solo limpiar las referencias
    markerGroup.value = null;
    paintedChargerIds.value.clear();
    if (selectedPoint.value) {
      closeModal();
    }
    return;
  }
  
  // Detener cualquier animación en curso antes de limpiar
  // Esto previene errores de _latLngToNewLayerPoint cuando el renderer es null
  try {
    // Detener animaciones de zoom
    if ((map as any)._animatingZoom) {
      map.stop();
    }
    // Detener animaciones de pan
    if ((map as any)._panAnim) {
      (map as any)._panAnim.stop();
    }
    // Detener cualquier otra animación
    if ((map as any).stop) {
      (map as any).stop();
    }
    // Forzar que el mapa termine cualquier animación pendiente
    if ((map as any)._zoomAnimated) {
      (map as any)._zoomAnimated = false;
    }
    // Detener animaciones táctiles (especialmente importante en Android)
    if ((map as any)._touchZoom) {
      try {
        (map as any)._touchZoom._animating = false;
      } catch (e) {
        // Ignorar errores
      }
    }
  } catch (e) {
    // Ignorar errores al detener animaciones
  }
  
  // PRIMERO: Limpiar solo los marcadores de cargadores, preservando la capa y el marcador de ubicación del usuario
  if (markerGroup.value) {
    try {
      // Obtener referencias al marcador y círculo de ubicación del usuario
      const userMarker = userLocationMarker.value;
      const userCircle = userLocationCircle.value;
      
      // Obtener todos los marcadores del grupo
      const layersToRemove: any[] = [];
      markerGroup.value.eachLayer((layer: any) => {
        // No incluir el marcador ni el círculo de ubicación del usuario
        if (layer !== userMarker && layer !== userCircle) {
          layersToRemove.push(layer);
        }
      });
      
      // Remover solo los marcadores de cargadores (excluyendo el marcador de ubicación del usuario)
      layersToRemove.forEach((layer: any) => {
        try {
          markerGroup.value?.removeLayer(layer);
        } catch (e) {
          // Ignorar errores
        }
      });
      
      // NO remover el grupo del mapa - lo preservamos para reutilizarlo
      // NO resetear markerGroup.value - lo mantenemos para reutilizarlo
    } catch (error) {
      console.warn('Error limpiando marcadores de cargadores:', error);
    }
  }
  
  // TERCERO: Iterar sobre todas las capas del mapa y eliminar marcadores huérfanos (solo de cargadores)
  try {
    const allLayers: any[] = [];
    map.eachLayer((layer: any) => {
      // Procesar marcadores individuales que no estén en markerGroup
      if (layer instanceof L.Marker) {
        // Solo añadir si no está en markerGroup (marcadores huérfanos)
        if (!markerGroup.value || !markerGroup.value.hasLayer(layer)) {
          allLayers.push(layer);
        }
      }
      // También procesar LayerGroups que puedan contener marcadores (excepto markerGroup)
      else if (layer instanceof L.LayerGroup && layer !== markerGroup.value) {
        // Verificar si es un grupo de marcadores de cargadores
        let hasChargerMarkers = false;
        try {
          layer.eachLayer((sublayer: any) => {
            if (sublayer instanceof L.Marker) {
              hasChargerMarkers = true;
            }
          });
        } catch (e) {
          // Ignorar errores
        }
        if (hasChargerMarkers) {
          allLayers.push(layer);
        }
      }
    });
    
    // Eliminar solo las capas huérfanas encontradas
    allLayers.forEach((layer: any) => {
      try {
        map.removeLayer(layer);
      } catch (e) {
        // Ignorar errores
      }
    });
  } catch (error) {
    console.warn('Error limpiando capas adicionales:', error);
  }
  
  // CUARTO: Limpiar el DOM directamente solo de marcadores de cargadores
  try {
    const markerPane = map.getPane('markerPane');
    if (markerPane) {
      // Obtener todos los elementos de marcadores
      const markerElements = markerPane.querySelectorAll('.leaflet-marker-icon');
      markerElements.forEach((element: any) => {
        try {
          // No eliminar marcadores de ubicación del usuario (tienen clase user-location-marker)
          const isUserLocationMarker = element.closest('.user-location-marker') !== null;
          
          if (!isUserLocationMarker && element.parentNode) {
            element.parentNode.removeChild(element);
          }
        } catch (e) {
          // Ignorar errores
        }
      });
    }
  } catch (e) {
    // Ignorar errores al limpiar el DOM
  }
  
  // Limpiar el Set de IDs pintados
  paintedChargerIds.value.clear();
  
  // Cerrar el panel flotante si está abierto
  if (selectedPoint.value) {
    closeModal();
  }
  
  
  // Forzar una actualización visual del mapa
  try {
    map.invalidateSize();
  } catch (e) {
    // Ignorar errores
  }
}

// Crear o reiniciar los marcadores en el mapa
function createOrUpdateMarkers() {
  const map = lmap.value?.leafletObject;
  if (!map) {
    console.warn('El mapa no está disponible aún');
    return;
  }

  // No crear marcadores si estamos en proceso de limpieza
  if (isClearingMarkers.value) {
    return;
  }

  // Verificar que hay cargadores antes de crear los marcadores
  if (!chargers.value || chargers.value.length === 0) {
    // Si no hay cargadores, limpiar el mapa
    if (!isIncrementalUpdate.value) {
      clearMapMarkers();
    }
    return;
  }

  // Limpiar marcadores anteriores si es actualización completa
  if (!isIncrementalUpdate.value && markerGroup.value) {
    // Limpiar solo los marcadores de cargadores, no la capa ni el marcador de ubicación del usuario
    if (markerGroup.value) {
      // Obtener referencias al marcador y círculo de ubicación del usuario
      const userMarker = userLocationMarker.value;
      const userCircle = userLocationCircle.value;
      
      const layersToRemove: any[] = [];
      markerGroup.value.eachLayer((layer: any) => {
        // No incluir el marcador ni el círculo de ubicación del usuario
        if (layer !== userMarker && layer !== userCircle) {
          layersToRemove.push(layer);
        }
      });
      layersToRemove.forEach((layer: any) => {
        markerGroup.value?.removeLayer(layer);
      });
    }
    paintedChargerIds.value.clear();
  }

  // Obtener o crear la capa única de marcadores
  const group = getOrCreateMarkerLayer();
  
  // Verificar que el grupo existe antes de continuar
  if (!group) {
    console.warn('No se pudo crear el grupo de marcadores');
    return;
  }
  let newMarkersCount = 0;

  // Para actualizaciones completas, reagrupar todos los cargadores
  // Para actualizaciones incrementales, solo procesar los nuevos
  const chargersToProcess = isIncrementalUpdate.value
    ? chargers.value.filter(charger => !paintedChargerIds.value.has(charger.id))
    : chargers.value;

  if (chargersToProcess.length === 0) {
    return;
  }

  // Agrupar cargadores cercanos (dentro de 10 metros)
  const chargerGroups = groupNearbyChargers(chargersToProcess, 10);


  // Crear marcadores para cada grupo
  chargerGroups.forEach(chargerGroup => {
    try {
      // Validar coordenadas del centro
      const lat = chargerGroup.centerLat;
      const lng = chargerGroup.centerLng;

      if (
        isNaN(lat) ||
        isNaN(lng) ||
        lat < -90 || lat > 90 ||
        lng < -180 || lng > 180
      ) {
        console.warn(`Grupo tiene coordenadas inválidas: lat=${lat}, lng=${lng}`);
        return;
      }

      // Crear objeto LatLng explícitamente para asegurar que sea válido
      const latLng = L.latLng(lat, lng);

      // Verificar que las coordenadas se crearon correctamente
      if (!latLng || isNaN(latLng.lat) || isNaN(latLng.lng)) {
        console.warn(`No se pudieron crear coordenadas válidas para el grupo`);
        return;
      }

      // Determinar el color: verde si alguno está libre, rojo si todos están ocupados
      const libreValue = chargerGroup.hasLibre ? 1 : 0;

      // Crear el marcador con el objeto LatLng explícito
      const marker = L.marker(latLng, {
        icon: getChargerIcon(libreValue, chargerGroup.count)
      });

      // Verificar que el marcador tiene coordenadas válidas
      const markerLatLng = marker.getLatLng();
      if (!markerLatLng || isNaN(markerLatLng.lat) || isNaN(markerLatLng.lng)) {
        console.warn(`Marcador creado sin coordenadas válidas para el grupo`);
        return;
      }

      // Almacenar los IDs de los cargadores del grupo en los datos del marcador
      (marker as any).chargerIds = chargerGroup.chargers.map(c => c.id);
      // Almacenar también el grupo completo de cargadores en el marcador
      (marker as any).chargerGroup = chargerGroup.chargers;

      // Manejar clic en el marcador - mostrar el primer cargador del grupo
      marker.on('click', (e: L.LeafletMouseEvent) => {
        // Verificar que el clic fue realmente en el marcador y no en el mapa
        const target = (e.originalEvent?.target as HTMLElement) || null;
        if (!target) {
          return;
        }

        // Verificar que el clic fue realmente en el marcador
        const isMarkerElement = target.closest('.leaflet-marker-icon, .charger-marker');
        if (!isMarkerElement) {
          return;
        }

        // Detener la propagación del evento para evitar que se propague al mapa
        L.DomEvent.stop(e);
        L.DomEvent.stopPropagation(e);
        // Si hay múltiples cargadores, mostrar el primero
        const firstChargerId = chargerGroup.chargers[0].id;
        selectPoint(firstChargerId, chargerGroup.chargers);
      });

      group.addLayer(marker);

      // Marcar todos los cargadores del grupo como pintados
      chargerGroup.chargers.forEach(charger => {
        paintedChargerIds.value.add(charger.id);
      });

      newMarkersCount++;
    } catch (error) {
      console.error(`Error creando marcador para grupo:`, error);
    }
  });

  // Si se agregaron nuevos marcadores, loguear
  if (newMarkersCount > 0) {
  }
}

function selectPoint(pointId: string, chargerGroup?: ChargerItem[]) {
  selectedPoint.value = pointId;

  // Si se proporciona un grupo, almacenarlo
  if (chargerGroup && chargerGroup.length > 0) {
    selectedChargerGroup.value = chargerGroup;
    // Encontrar el índice del cargador seleccionado en el grupo
    const index = chargerGroup.findIndex(c => c.id === pointId);
    currentChargerIndex.value = index >= 0 ? index : 0;
  } else {
    // Si no hay grupo, buscar el cargador individual
    const charger = chargers.value.find(c => c.id === pointId);
    if (charger) {
      selectedChargerGroup.value = [charger];
      currentChargerIndex.value = 0;
    }
  }

  // Actualizar los datos del punto seleccionado
  const charger = chargers.value.find(c => c.id === pointId);
  if (charger) {
    
    selectedPointData.value = {
      name: charger.name,
      distanceKm: charger.distanceKm,
      priceKwh: charger.priceKwh,
      totalCost: charger.totalCost,
      powerKw: charger.powerKw,
      chargeTimeMin: charger.chargeTimeMin,
      latitud: charger.latitud,
      longitud: charger.longitud,
      libre: charger.libre,
      total: charger.total
    };
  }
}

// Función para navegar al siguiente cargador del grupo
function navigateToNext() {
  if (selectedChargerGroup.value.length === 0) return;

  currentChargerIndex.value = (currentChargerIndex.value + 1) % selectedChargerGroup.value.length;
  const nextCharger = selectedChargerGroup.value[currentChargerIndex.value];
  if (nextCharger) {
    selectPoint(nextCharger.id, selectedChargerGroup.value);
  }
}

// Función para navegar al cargador anterior del grupo
function navigateToPrevious() {
  if (selectedChargerGroup.value.length === 0) return;

  currentChargerIndex.value = currentChargerIndex.value === 0
    ? selectedChargerGroup.value.length - 1
    : currentChargerIndex.value - 1;
  const prevCharger = selectedChargerGroup.value[currentChargerIndex.value];
  if (prevCharger) {
    selectPoint(prevCharger.id, selectedChargerGroup.value);
  }
}

function closeModal() {
  selectedPoint.value = null;
  selectedChargerGroup.value = [];
  currentChargerIndex.value = 0;
}

// Manejar errores de navegación desde ChargerListItem
function handleNavigationError(error: string) {
  navigationError.value = error;
}

// ========== FUNCIONES DE GEOLOCALIZACIÓN PERSONALIZADA ==========

// Función para crear el HTML del icono de ubicación con triángulo de orientación
function createLocationIconHtml(heading: number | null): string {
  // Si no hay heading, mostrar solo el círculo
  if (heading === null || isNaN(heading)) {
    return '<div style="background-color: #3388ff; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>';
  }

  // Crear el HTML con círculo y triángulo rotado
  // Implementación similar a Leaflet locate control
  // El triángulo apunta hacia abajo inicialmente (border-bottom) y rota alrededor del centro del círculo
  // El heading viene en grados (0-360), donde 0 es el norte, 90 es este, 180 es sur, 270 es oeste
  // El círculo está centrado en (20px, 20px) del contenedor de 40x40px
  // El triángulo está posicionado arriba del círculo y rota alrededor del centro (20px, 20px)
  // En iOS: el sistema es correcto (270° = oeste), usamos heading directamente sin invertir
  // En Android: el sistema está invertido (90° = oeste), convertimos: (360 - heading) % 360, sin invertir rotación
  let correctedHeading = heading;
  if (!isIOS.value) {
    // Android: convertir el sistema de coordenadas (90° oeste → 270° oeste)
    // Si 90° es oeste, convertimos: (360 - heading) % 360
    correctedHeading = (360 - heading) % 360;
  }
  // iOS: rotación directa, Android: rotación directa después de la conversión
  const rotation = correctedHeading;
  
  return `
    <div style="position: relative; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;">
      <!-- Círculo central -->
      <div style="
        background-color: #3388ff;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        position: relative;
        z-index: 3;
      "></div>
      <!-- Triángulo que indica la dirección (implementación estilo Leaflet) -->
      <!-- El triángulo apunta hacia abajo (border-bottom) y rota alrededor del centro del círculo -->
      <div style="
        position: absolute;
        width: 0;
        height: 0;
        border-left: 7px solid transparent;
        border-right: 7px solid transparent;
        border-bottom: 18px solid #3388ff;
        transform: rotate(${rotation}deg);
        transform-origin: 50% 20px;
        top: 0;
        left: 50%;
        margin-left: -7px;
        z-index: 2;
        filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
      "></div>
    </div>
  `;
}

// Crear o actualizar el marcador y círculo de ubicación del usuario
function updateUserLocationMarker(lat: number, lon: number, accuracy?: number) {
  const map = lmap.value?.leafletObject;
  if (!map) return;

  const group = getOrCreateMarkerLayer();
  const latLng = L.latLng(lat, lon);

  // Crear o actualizar el marcador de ubicación
  if (!userLocationMarker.value) {
    // Crear icono personalizado para el marcador de ubicación con triángulo de orientación
    const locationIcon = L.divIcon({
      className: 'user-location-marker',
      html: createLocationIconHtml(userHeading.value),
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    });

    userLocationMarker.value = L.marker(latLng, { icon: locationIcon });
    group.addLayer(userLocationMarker.value as any);
  } else {
    userLocationMarker.value.setLatLng(latLng);
    // Actualizar el icono si cambió el heading
    const newIcon = L.divIcon({
      className: 'user-location-marker',
      html: createLocationIconHtml(userHeading.value),
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    });
    userLocationMarker.value.setIcon(newIcon);
  }

  // Crear o actualizar el círculo de precisión
  const radius = accuracy || 50; // Radio por defecto de 50 metros si no hay precisión
  if (!userLocationCircle.value) {
    userLocationCircle.value = L.circle(latLng, {
      radius: radius,
      fillColor: '#3388ff',
      fillOpacity: 0.2,
      color: '#3388ff',
      weight: 2,
      opacity: 0.5
    });
    group.addLayer(userLocationCircle.value as any);
  } else {
    userLocationCircle.value.setLatLng(latLng);
    userLocationCircle.value.setRadius(radius);
  }
}

// Eliminar el marcador y círculo de ubicación del usuario
function removeUserLocationMarker() {
  const group = getOrCreateMarkerLayer();
  
  if (userLocationMarker.value) {
    group.removeLayer(userLocationMarker.value as any);
    userLocationMarker.value = null;
  }
  
  if (userLocationCircle.value) {
    group.removeLayer(userLocationCircle.value as any);
    userLocationCircle.value = null;
  }
}

// Manejar el clic en el botón de ubicación
function handleLocationButtonClick() {
  const map = lmap.value?.leafletObject;
  if (!map) return;

  // Si está siguiendo, detener el seguimiento
  if (isLocateFollowing.value) {
    stopLocationTracking();
    return;
  }

  // Si no está siguiendo, iniciar la búsqueda de ubicación
  userClickedLocateButton.value = true;
  startLocationTracking();
}

// Iniciar el seguimiento de ubicación
function startLocationTracking() {
  if (!navigator.geolocation) {
    const errorMessage = t("components.chargerresultsmapview.navegador_no_soporta_geolocalizacion");
    locationError.value = errorMessage;
    locationErrorFromButton.value = errorMessage;
    return;
  }

  isLocating.value = true;
  locationError.value = null;
  locationErrorFromButton.value = null;

  const options: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 60000
  };

  // Obtener ubicación una vez
  navigator.geolocation.getCurrentPosition(
    (position) => {
      handleLocationSuccess(position);
      // Iniciar seguimiento continuo
      startWatchingLocation();
    },
    (error) => {
      handleLocationError(error);
    },
    options
  );
}

// Iniciar el seguimiento continuo de ubicación
function startWatchingLocation() {
  if (!navigator.geolocation) return;

  const options: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0 // Siempre obtener ubicación fresca durante el seguimiento
  };

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      handleLocationSuccess(position, false); // false = no centrar automáticamente
    },
    (error) => {
      handleLocationError(error);
    },
    options
  );

  locationWatchId.value = watchId;
  isLocateFollowing.value = true;
  isLocating.value = false;
}

// Detener el seguimiento de ubicación
function stopLocationTracking() {
  if (locationWatchId.value !== null) {
    navigator.geolocation.clearWatch(locationWatchId.value);
    locationWatchId.value = null;
  }
  
  isLocateFollowing.value = false;
  isLocating.value = false;
  userClickedLocateButton.value = false;
}

// Manejar éxito en la obtención de ubicación
async function handleLocationSuccess(position: GeolocationPosition, shouldCenter: boolean = true) {
  const map = lmap.value?.leafletObject;
  if (!map) return;

  const lat = position.coords.latitude;
  const lon = position.coords.longitude;
  const accuracy = position.coords.accuracy;

  const newLocation = { lat, lon };
  userLocation.value = newLocation;

  // Capturar heading si está disponible
  if (position.coords.heading !== null && !isNaN(position.coords.heading)) {
    userHeading.value = position.coords.heading;
  }

  // Actualizar el marcador y círculo
  updateUserLocationMarker(lat, lon, accuracy);

  // Limpiar errores
  locationError.value = null;
  locationErrorFromButton.value = null;

  // Centrar el mapa SOLO si el usuario hizo clic explícitamente en el botón
  if (shouldCenter && userClickedLocateButton.value) {
    map.flyTo([lat, lon], zoomMapa, {
      duration: 1.0
    });
  } else if (!shouldCenter) {
    // Si no se debe centrar (actualización durante seguimiento), no hacer nada
  }

  // Actualizar localStorage
  try {
    const storedData = localStorage.getItem('chargerSearchData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      parsedData.location = newLocation;
      localStorage.setItem('chargerSearchData', JSON.stringify(parsedData));
      
      // Si la ubicación cambió significativamente, recargar resultados
      // Pero solo si el usuario hizo clic explícitamente en el botón
      if (searchData.value?.location && userClickedLocateButton.value) {
        const oldLat = searchData.value.location.lat;
        const oldLon = searchData.value.location.lon;
        const distance = Math.sqrt(
          Math.pow(newLocation.lat - oldLat, 2) + Math.pow(newLocation.lon - oldLon, 2)
        );
        // Si la distancia es mayor a ~1km (aproximadamente 0.009 grados), recargar
        if (distance > 0.009) {
          searchData.value.location = newLocation;
          await loadSearchResults();
        }
      }
    }
  } catch (error) {
    console.warn('Error actualizando ubicación en localStorage:', error);
  }

  isLocating.value = false;
}

// Manejar errores en la obtención de ubicación
function handleLocationError(error: GeolocationPositionError) {
  isLocating.value = false;
  isLocateFollowing.value = false;
  userClickedLocateButton.value = false;

  let errorMessage = t("components.chargerresultsmapview.no_pudo_acceder_ubicacion");

  switch (error.code) {
    case 1: // PERMISSION_DENIED
      errorMessage = t("components.chargerresultsmapview.permiso_ubicacion_denegado");
      break;
    case 2: // POSITION_UNAVAILABLE
      errorMessage = t("components.chargerresultsmapview.ubicacion_no_disponible");
      break;
    case 3: // TIMEOUT
      errorMessage = t("components.chargerresultsmapview.tiempo_espera_agotado");
      break;
    default:
      errorMessage = t("components.chargerresultsmapview.no_pudo_acceder_ubicacion");
      break;
  }

  locationError.value = errorMessage;
  locationErrorFromButton.value = errorMessage;
}

// ========== FIN FUNCIONES DE GEOLOCALIZACIÓN PERSONALIZADA ==========

// Cargar filtros desde localStorage
function loadFiltersFromStorage() {
  try {
    const storedFilters = localStorage.getItem('chargerActiveFilters');
    if (storedFilters) {
      activeFilters.value = JSON.parse(storedFilters);
    }
  } catch (error) {
    console.warn('Error cargando filtros desde localStorage:', error);
  }
}

// Guardar filtros en localStorage
function saveFiltersToStorage(filters: ActiveFilters) {
  try {
    localStorage.setItem('chargerActiveFilters', JSON.stringify(filters));
  } catch (error) {
    console.warn('Error guardando filtros en localStorage:', error);
  }
}

// Abrir el modal de filtros
function openFilters() {
  showFilters.value = true;
}

// Manejar la aplicación de filtros
async function handleApplyFilters(filters: ActiveFilters) {
  activeFilters.value = filters;
  // Guardar filtros en localStorage para compartir entre vistas
  saveFiltersToStorage(filters);
  
  // Activar flag para evitar recargas automáticas
  isClearingMarkers.value = true;
  
  // RESETEAR TODOS LOS DATOS Y MARCADORES ANTERIORES
  
  // 1. Limpiar el array de cargadores (datos anteriores)
  chargers.value = [];
  
  // 2. Limpiar IDs de cargadores pintados
  paintedChargerIds.value.clear();
  
  // 3. Resetear marcadores agrupados anteriores
  selectedChargerGroup.value = [];
  currentChargerIndex.value = 0;
  selectedPoint.value = null;
  
  // 4. Limpiar completamente el mapa de todos los marcadores anteriores (incluyendo agrupados)
  clearMapMarkers();
  
  // Desactivar flag ANTES de recargar para que createOrUpdateMarkers pueda ejecutarse
  isClearingMarkers.value = false;
  
  // LUEGO: Recargar resultados con los nuevos filtros (reemplazo completo)
  isIncrementalUpdate.value = false;
  await loadSearchResults();
}

// Función de debounce para llamadas a la API cuando cambia el radio o el centro
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
function debouncedLoadSearchResults(reason: string = '') {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(async () => {
    isIncrementalUpdate.value = true; // Modo incremental
    await loadSearchResults();
  }, 500); // Esperar 500ms después del último cambio
}

// Calcular y mostrar la distancia desde la ubicación del usuario hasta el centro del mapa
function calculateDistanceToMapCenter() {
  const map = lmap.value?.leafletObject;
  if (!map) {
    console.warn('El mapa no está disponible');
    return;
  }

  try {
    // Obtener el centro del mapa visible
    const mapCenter = map.getCenter();

    // Calcular el radio visible del mapa (desde el centro hasta el borde superior)
    const bounds = map.getBounds();
    const northEdge = bounds.getNorth();
    const eastEdge = bounds.getEast();
    
    // Calcular distancia desde el centro hasta el borde norte (vertical)
    const centerToNorth = L.latLng(mapCenter.lat, mapCenter.lng);
    const northEdgePoint = L.latLng(northEdge, mapCenter.lng); // Misma longitud, borde norte
    const radiusNorthMeters = centerToNorth.distanceTo(northEdgePoint);

    // Calcular distancia desde el centro hasta el borde este (horizontal)
    const eastEdgePoint = L.latLng(mapCenter.lat, eastEdge); // Misma latitud, borde este
    const radiusEastMeters = centerToNorth.distanceTo(eastEdgePoint);

    // El radio más grande será el que determine el área visible completa
    const maxRadiusMeters = Math.max(radiusNorthMeters, radiusEastMeters);

    // Actualizar el radio visible del mapa (para usar en la API)
    const previousRadius = mapVisibleRadius.value;
    mapVisibleRadius.value = maxRadiusMeters;


    // Detectar si el radio cambió significativamente (más del 10%)
    const radiusChange = Math.abs(previousRadius - maxRadiusMeters);
    const radiusChangePercent = previousRadius > 0 ? (radiusChange / previousRadius) * 100 : 100;
    let shouldReload = radiusChangePercent > 10;
    let reloadReason = '';

    if (shouldReload) {
      reloadReason = `Radio cambió ${formatNumberWithComma(radiusChangePercent, 1)}%`;
    }

    // Detectar si el centro del mapa cambió significativamente
    if (previousMapCenter.value) {
      const previousCenter = L.latLng(previousMapCenter.value.lat, previousMapCenter.value.lng);
      const currentCenter = L.latLng(mapCenter.lat, mapCenter.lng);
      const centerDistanceMeters = previousCenter.distanceTo(currentCenter);

      // Si el centro se movió más del 20% del radio actual, recargar cargadores
      const centerChangeThreshold = maxRadiusMeters * 0.2; // 20% del radio

      if (centerDistanceMeters > centerChangeThreshold) {
        shouldReload = true;
        reloadReason = reloadReason
          ? `${reloadReason} y centro se movió ${Math.round(centerDistanceMeters)}m`
          : `Centro del mapa se movió ${Math.round(centerDistanceMeters)}m`;
      }
    }

    // Actualizar el centro anterior
    previousMapCenter.value = {
      lat: mapCenter.lat,
      lng: mapCenter.lng
    };

    // Recargar cargadores si hubo cambios significativos
    // Pero NO en la primera carga inicial (para evitar llamadas duplicadas)
    if (shouldReload && !isInitialLoad.value) {
      debouncedLoadSearchResults(reloadReason);
    }

  } catch (error) {
    console.warn('Error calculando la distancia:', error);
  }
}

// Parchear métodos de Leaflet para prevenir errores con _leaflet_events undefined
function patchLeafletEventHandling() {
  if (!L) return;

  // Parchear el método off de Evented para manejar _leaflet_events undefined
  if ((L as any).Evented && (L as any).Evented.prototype) {
    const originalOff = (L as any).Evented.prototype.off;
    if (originalOff && !(originalOff as any)._patched) {
      (L as any).Evented.prototype.off = function (type?: string, fn?: Function, context?: any) {
        try {
          // Validar que _leaflet_events existe antes de acceder
          if (!this || !this._leaflet_events) {
            return this;
          }
          return originalOff.call(this, type, fn, context);
        } catch (error) {
          // Si hay error, simplemente retornar this para no romper la cadena
          return this;
        }
      };
      (L as any).Evented.prototype.off._patched = true;
    }

    // Parchear también el método onRemove de Marker que puede causar problemas
    if ((L as any).Marker && (L as any).Marker.prototype) {
      const originalOnRemove = (L as any).Marker.prototype.onRemove;
      if (originalOnRemove && !(originalOnRemove as any)._patched) {
        (L as any).Marker.prototype.onRemove = function (map: any) {
          try {
            // Validar que el objeto tiene _leaflet_events antes de proceder
            if (!this || !this._leaflet_events) {
              return this;
            }
            return originalOnRemove.call(this, map);
          } catch (error) {
            // Si hay error, simplemente retornar this
            return this;
          }
        };
        (L as any).Marker.prototype.onRemove._patched = true;
      }
    }

    // Parchear removeLayer de LayerGroup para manejar mejor la eliminación
    if ((L as any).LayerGroup && (L as any).LayerGroup.prototype) {
      const originalRemoveLayer = (L as any).LayerGroup.prototype.removeLayer;
      if (originalRemoveLayer && !(originalRemoveLayer as any)._patched) {
        (L as any).LayerGroup.prototype.removeLayer = function (layer: any) {
          try {
            // Validar que layer existe y es válido
            if (!layer) {
              return this;
            }
            // Validar que layer tiene _leaflet_events antes de intentar removerlo
            if (layer._leaflet_events === undefined && layer._leaflet_id === undefined) {
              // Si el layer ya fue eliminado, simplemente retornar
              return this;
            }
            return originalRemoveLayer.call(this, layer);
          } catch (error) {
            // Si hay error, simplemente retornar this
            return this;
          }
        };
        (L as any).LayerGroup.prototype.removeLayer._patched = true;
      }
    }
  }
}

// Parchear Layer._animateZoom para prevenir errores cuando el renderer es null durante la animación
function patchLeafletLayerAnimation() {
  if (!L) return;

  // Parchear Map._animateZoom primero, ya que puede disparar eventos que llaman a Layer._animateZoom
  if ((L as any).Map && (L as any).Map.prototype) {
    const originalMapAnimateZoom = (L as any).Map.prototype._animateZoom;
    if (originalMapAnimateZoom && !(originalMapAnimateZoom as any)._patched) {
      (L as any).Map.prototype._animateZoom = function (e: any) {
        try {
          // Validar que el renderer existe antes de animar
          if (!this._renderer) {
            // Si no hay renderer, simplemente retornar sin animar
            return;
          }

          // Validar que el renderer tiene el método necesario
          if (this._renderer && typeof this._renderer._latLngToNewLayerPoint !== 'function') {
            return;
          }

          // Llamar al método original
          return originalMapAnimateZoom.call(this, e);
        } catch (error: any) {
          // Capturar errores relacionados con renderer null
          const errorMessage = error?.message || '';
          const errorStack = error?.stack || '';
          const errorDetails = error?.details || '';

          // Incluir el error específico "null is not an object (evaluating 'this._map._latLngToNewLayerPoint')"
          const isRendererError =
            errorMessage.includes('_latLngToNewLayerPoint') ||
            errorMessage.includes('_map._latLngToNewLayerPoint') ||
            errorMessage.includes('Cannot read properties of null') ||
            errorMessage.includes('Cannot read property') ||
            errorMessage.includes('null is not an object') ||
            errorMessage.includes('null') ||
            errorMessage.includes('undefined') ||
            errorStack.includes('_latLngToNewLayerPoint') ||
            errorStack.includes('_animateZoom') ||
            errorDetails.includes('_latLngToNewLayerPoint') ||
            errorDetails.includes('null is not an object');

          if (isRendererError) {
            // Silenciar estos errores - el renderer se inicializará después
            // No hacer nada, simplemente retornar
            return;
          }

          // Para otros errores, loguear para depuración
          console.warn('Error en Map._animateZoom:', error);
          return;
        }
      };
      (L as any).Map.prototype._animateZoom._patched = true;
    }
  }

  // Parchear Layer._animateZoom para manejar renderer null
  if ((L as any).Layer && (L as any).Layer.prototype) {
    const originalAnimateZoom = (L as any).Layer.prototype._animateZoom;
    if (originalAnimateZoom && !(originalAnimateZoom as any)._patched) {
      (L as any).Layer.prototype._animateZoom = function (e: any) {
        try {
          // Validar que el mapa existe
          if (!this._map) {
            return;
          }

          // Validar que el renderer existe - puede ser null durante transiciones
          const renderer = this._map._renderer;
          if (!renderer) {
            // Si no hay renderer, simplemente retornar sin animar
            return;
          }

          // Validar que el renderer tiene el método necesario
          if (typeof renderer._latLngToNewLayerPoint !== 'function') {
            return;
          }

          // Validar que el renderer está completamente inicializado
          // Esto previene errores cuando el renderer está en un estado inconsistente
          if (!renderer._container) {
            return;
          }

          // Llamar al método original - el try-catch capturará cualquier error
          return originalAnimateZoom.call(this, e);
        } catch (error: any) {
          // Capturar TODOS los errores relacionados con null/undefined y _latLngToNewLayerPoint
          const errorMessage = error?.message || '';
          const errorStack = error?.stack || '';
          const errorDetails = error?.details || '';

          // Verificar si es un error relacionado con renderer null o _latLngToNewLayerPoint
          // Incluir el error específico "null is not an object (evaluating 'this._map._latLngToNewLayerPoint')"
          const isRendererError =
            errorMessage.includes('_latLngToNewLayerPoint') ||
            errorMessage.includes('_map._latLngToNewLayerPoint') ||
            errorMessage.includes('Cannot read properties of null') ||
            errorMessage.includes('Cannot read property') ||
            errorMessage.includes('null is not an object') ||
            errorMessage.includes('null') ||
            errorMessage.includes('undefined') ||
            errorStack.includes('_latLngToNewLayerPoint') ||
            errorStack.includes('_animateZoom') ||
            errorDetails.includes('_latLngToNewLayerPoint') ||
            errorDetails.includes('null is not an object');

          if (isRendererError) {
            // Silenciar estos errores - el renderer se inicializará después o la animación no es crítica
            // No hacer nada, simplemente retornar
            return;
          }

          // Para otros errores, también silenciar pero loguear para depuración
          console.warn('Error en Layer._animateZoom:', error);
          return;
        }
      };
      (L as any).Layer.prototype._animateZoom._patched = true;
    }

    // Parchear Layer._reset para manejar renderer null durante la animación
    const originalReset = (L as any).Layer.prototype._reset;
    if (originalReset && !(originalReset as any)._patched) {
      (L as any).Layer.prototype._reset = function () {
        try {
          // Validar que el mapa y el renderer existen antes de resetear
          if (!this._map || !this._map._renderer) {
            return;
          }

          // Llamar al método original solo si el renderer está disponible
          return originalReset.call(this);
        } catch (error) {
          // Silenciar errores para no romper la aplicación
          console.warn('Error en Layer._reset:', error);
          return;
        }
      };
      (L as any).Layer.prototype._reset._patched = true;
    }
  }

  // Parchear los renderers para manejar acceso a _latLngToNewLayerPoint cuando el renderer es null
  // Esto puede ocurrir durante transiciones de renderer o cuando se accede desde una capa
  // Parchear en el Map para interceptar accesos a _renderer._latLngToNewLayerPoint
  if ((L as any).Map && (L as any).Map.prototype) {
    // Interceptar accesos al renderer durante animaciones
    const originalGetRenderer = (L as any).Map.prototype._getRenderer;
    // No parcheamos _getRenderer directamente, sino que validamos en _animateZoom
  }

  // Parchear directamente los renderers SVG y Canvas si existen
  const rendererTypes = ['SVG', 'Canvas'];
  rendererTypes.forEach((rendererType: string) => {
    if ((L as any)[rendererType] && (L as any)[rendererType].prototype) {
      const originalLatLngToNewLayerPoint = (L as any)[rendererType].prototype._latLngToNewLayerPoint;
      if (originalLatLngToNewLayerPoint && !(originalLatLngToNewLayerPoint as any)._patched) {
        (L as any)[rendererType].prototype._latLngToNewLayerPoint = function (latlng: any, zoom: any, center: any) {
          try {
            // Validar que el renderer (this) existe y no es null
            if (!this || this === null) {
              // Retornar un punto por defecto si no hay renderer
              return { x: 0, y: 0 };
            }

            // Validar que el mapa existe
            if (!this._map) {
              return { x: 0, y: 0 };
            }

            // Validar que el mapa tiene un renderer válido (puede ser null durante transiciones)
            const mapRenderer = this._map._renderer;
            if (!mapRenderer) {
              // Si no hay renderer en el mapa, retornar punto por defecto
              return { x: 0, y: 0 };
            }

            // Validar que el renderer del mapa coincide con este renderer
            if (mapRenderer !== this) {
              // Si el renderer del mapa no coincide con este renderer, retornar punto por defecto
              return { x: 0, y: 0 };
            }

            // Validar que el renderer tiene las propiedades necesarias antes de llamar al método original
            // Esto previene errores cuando el renderer está en un estado inconsistente
            // Solo validar _container ya que _zoomAnimated puede no estar siempre presente
            if (!this._container) {
              // Si el renderer no está completamente inicializado, retornar punto por defecto
              return { x: 0, y: 0 };
            }

            // Llamar al método original
            return originalLatLngToNewLayerPoint.call(this, latlng, zoom, center);
          } catch (error: any) {
            // Capturar errores relacionados con null/undefined
            const errorMessage = error?.message || '';
            const errorStack = error?.stack || '';

            const isRendererError =
              errorMessage.includes('_latLngToNewLayerPoint') ||
              errorMessage.includes('Cannot read properties of null') ||
              errorMessage.includes('Cannot read property') ||
              errorMessage.includes('null') ||
              errorMessage.includes('undefined') ||
              errorStack.includes('_latLngToNewLayerPoint');

            if (isRendererError) {
              // Retornar un punto por defecto si hay error relacionado con renderer
              return { x: 0, y: 0 };
            }

            // Para otros errores, también retornar punto por defecto pero loguear
            console.warn('Error en renderer._latLngToNewLayerPoint:', error);
            return { x: 0, y: 0 };
          }
        };
        (L as any)[rendererType].prototype._latLngToNewLayerPoint._patched = true;
      }
    }
  });

  // Parchear Marker._animateZoom específicamente para prevenir errores cuando se eliminan marcadores
  // Solución basada en: https://stackoverflow.com/questions/73542576/leaflet-error-when-zooming-after-closing-popup
  if ((L as any).Marker && (L as any).Marker.prototype) {
    const originalMarkerAnimateZoom = (L as any).Marker.prototype._animateZoom;
    if (originalMarkerAnimateZoom && !(originalMarkerAnimateZoom as any)._patched) {
      (L as any).Marker.prototype._animateZoom = function (opt: { zoom: number; center: L.LatLng }) {
        // Verificar que el mapa existe antes de intentar animar
        // Esto previene el error "Cannot read properties of null (reading '_latLngToNewLayerPoint')"
        // cuando se eliminan marcadores y luego se intenta hacer zoom
        if (!this._map) {
          return;
        }

        // Llamar al método original solo si el mapa existe
        const pos = this._map._latLngToNewLayerPoint(this._latlng, opt.zoom, opt.center).round();
        this._setPos(pos);
      };
      (L as any).Marker.prototype._animateZoom._patched = true;
    }
  }
}

// Parchear Circle._updateCircle para prevenir errores cuando los bounds no están inicializados
function patchLeafletCircle() {
  if (!L) return;

  // Parchear Circle._updateCircle para manejar bounds undefined
  if ((L as any).Circle && (L as any).Circle.prototype) {
    const originalUpdateCircle = (L as any).Circle.prototype._updateCircle;
    if (originalUpdateCircle && !(originalUpdateCircle as any)._patched) {
      (L as any).Circle.prototype._updateCircle = function () {
        try {
          // Validar que el mapa existe y está inicializado
          if (!this._map || !this._map._renderer) {
            return;
          }

          // Validar que el punto existe y tiene coordenadas válidas
          if (!this._point || this._point.x === undefined || this._point.y === undefined) {
            return;
          }

          // Llamar al método original solo si todo está válido
          return originalUpdateCircle.call(this);
        } catch (error) {
          // Silenciar errores para no romper la aplicación
          console.warn('Error en Circle._updateCircle:', error);
          return;
        }
      };
      (L as any).Circle.prototype._updateCircle._patched = true;
    }

    // Parchear Circle._updatePath para manejar bounds undefined
    const originalUpdatePath = (L as any).Circle.prototype._updatePath;
    if (originalUpdatePath && !(originalUpdatePath as any)._patched) {
      (L as any).Circle.prototype._updatePath = function () {
        try {
          // Validar que el mapa existe y está inicializado
          if (!this._map || !this._map._renderer) {
            return;
          }

          // Validar que el punto existe y tiene coordenadas válidas
          if (!this._point || this._point.x === undefined || this._point.y === undefined) {
            return;
          }

          // Llamar al método original solo si todo está válido
          return originalUpdatePath.call(this);
        } catch (error) {
          // Silenciar errores para no romper la aplicación
          console.warn('Error en Circle._updatePath:', error);
          return;
        }
      };
      (L as any).Circle.prototype._updatePath._patched = true;
    }

    // Parchear Circle._empty para manejar bounds undefined
    const originalEmpty = (L as any).Circle.prototype._empty;
    if (originalEmpty && !(originalEmpty as any)._patched) {
      (L as any).Circle.prototype._empty = function () {
        try {
          // Validar que el renderer existe antes de acceder a sus métodos
          if (!this._map || !this._map._renderer) {
            return;
          }

          // Validar que los bounds existen y son válidos antes de llamar al método original
          // El método _empty puede llamar a intersects que requiere bounds válidos
          if (this._bounds) {
            const bounds = this._bounds;
            // Validar que bounds tiene min y max válidos
            if (bounds.min !== undefined && bounds.max !== undefined) {
              const min = bounds.min;
              const max = bounds.max;
              // Si min o max no tienen x e y válidos, retornar sin ejecutar
              if (!min || min.x === undefined || min.y === undefined ||
                  !max || max.x === undefined || max.y === undefined) {
                return;
              }
            }
          }

          // Llamar al método original solo si todo está válido
          return originalEmpty.call(this);
        } catch (error) {
          // Silenciar errores para no romper la aplicación
          // No loguear el error ya que puede ser muy frecuente durante animaciones
          return;
        }
      };
      (L as any).Circle.prototype._empty._patched = true;
    }
  }
}

// Parchear LatLngBounds.intersects y Bounds.intersects una vez al inicio para prevenir errores
function patchLeafletIntersects() {
  if (!L) return;

  // Parchear LatLngBounds.intersects
  if ((L as any).LatLngBounds) {
    const originalLatLngIntersects = (L as any).LatLngBounds.prototype.intersects;
    if (originalLatLngIntersects && !(originalLatLngIntersects as any)._patched) {
      (L as any).LatLngBounds.prototype.intersects = function (other: any) {
        try {
          // Validar que 'this' tiene bounds válidos
          if (this._southWest) {
            const sw = this._southWest;
            if (!sw || sw.lat === undefined || sw.lat === null || isNaN(sw.lat) ||
              sw.lng === undefined || sw.lng === null || isNaN(sw.lng)) {
              return false;
            }
          }
          if (this._northEast) {
            const ne = this._northEast;
            if (!ne || ne.lat === undefined || ne.lat === null || isNaN(ne.lat) ||
              ne.lng === undefined || ne.lng === null || isNaN(ne.lng)) {
              return false;
            }
          }

          // Validar que 'other' existe
          if (!other) {
            return false;
          }

          // Si other es un LatLngBounds, validar sus propiedades
          if (other._southWest || other._northEast) {
            const sw = other._southWest;
            const ne = other._northEast;
            if (sw && (sw.lat === undefined || sw.lat === null || isNaN(sw.lat) ||
              sw.lng === undefined || sw.lng === null || isNaN(sw.lng))) {
              return false;
            }
            if (ne && (ne.lat === undefined || ne.lat === null || isNaN(ne.lat) ||
              ne.lng === undefined || ne.lng === null || isNaN(ne.lng))) {
              return false;
            }
          }

          // Si other tiene getBounds, obtener y validar sus bounds
          if (other && typeof other.getBounds === 'function') {
            try {
              const bounds = other.getBounds();
              if (!bounds) {
                return false;
              }
              if (bounds._southWest || bounds._northEast) {
                const sw = bounds._southWest;
                const ne = bounds._northEast;
                if (sw && (sw.lat === undefined || sw.lat === null || isNaN(sw.lat) ||
                  sw.lng === undefined || sw.lng === null || isNaN(sw.lng))) {
                  return false;
                }
                if (ne && (ne.lat === undefined || ne.lat === null || isNaN(ne.lat) ||
                  ne.lng === undefined || ne.lng === null || isNaN(ne.lng))) {
                  return false;
                }
              }
            } catch (e) {
              return false;
            }
          }

          // Llamar al método original
          return originalLatLngIntersects.call(this, other);
        } catch (error) {
          // Silenciar errores para no romper la aplicación
          return false;
        }
      };
      (L as any).LatLngBounds.prototype.intersects._patched = true;
    }
  }

  // Parchear Bounds.intersects (usado en spiderfy y otras operaciones de pantalla)
  if ((L as any).Bounds) {
    const originalBoundsIntersects = (L as any).Bounds.prototype.intersects;
    if (originalBoundsIntersects && !(originalBoundsIntersects as any)._patched) {
      (L as any).Bounds.prototype.intersects = function (other: any) {
        try {
          // Validar que 'this' existe
          if (!this) {
            return false;
          }

          // Validar que 'this' tiene propiedades válidas (ambas min y max deben existir)
          if (!this.min || !this.max) {
            return false;
          }

          const min = this.min;
          const max = this.max;

          // Validar que min existe y tiene x e y válidos
          if (!min || typeof min !== 'object' || 
              min.x === undefined || min.x === null || isNaN(min.x) ||
              min.y === undefined || min.y === null || isNaN(min.y)) {
            return false;
          }

          // Validar que max existe y tiene x e y válidos
          if (!max || typeof max !== 'object' ||
              max.x === undefined || max.x === null || isNaN(max.x) ||
              max.y === undefined || max.y === null || isNaN(max.y)) {
            return false;
          }

          // Validar que 'other' existe
          if (!other) {
            return false;
          }

          // Si other es un Bounds, validar que tiene tanto min como max válidos
          if (other.min !== undefined || other.max !== undefined) {
            // Si other tiene alguna propiedad min/max, ambas deben ser válidas
            if (!other.min || !other.max) {
              return false;
            }

            const otherMin = other.min;
            const otherMax = other.max;

            // Validar que otherMin existe y tiene x e y válidos
            if (!otherMin || typeof otherMin !== 'object' ||
                otherMin.x === undefined || otherMin.x === null || isNaN(otherMin.x) ||
                otherMin.y === undefined || otherMin.y === null || isNaN(otherMin.y)) {
              return false;
            }

            // Validar que otherMax existe y tiene x e y válidos
            if (!otherMax || typeof otherMax !== 'object' ||
                otherMax.x === undefined || otherMax.x === null || isNaN(otherMax.x) ||
                otherMax.y === undefined || otherMax.y === null || isNaN(otherMax.y)) {
              return false;
            }
          }

          // Llamar al método original solo si todas las validaciones pasaron
          return originalBoundsIntersects.call(this, other);
        } catch (error: any) {
          // Silenciar errores para no romper la aplicación
          // No loguear errores relacionados con propiedades undefined ya que son esperados
          const errorMessage = error?.message || '';
          const isExpectedError = 
            errorMessage.includes('Cannot read properties of undefined') ||
            errorMessage.includes('Cannot read property') ||
            errorMessage.includes('undefined');
          
          if (!isExpectedError) {
            console.warn('Error en Bounds.intersects:', error);
          }
          return false;
        }
      };
      (L as any).Bounds.prototype.intersects._patched = true;
    }
  }
}

// Watcher para recargar filtros cuando cambia la ruta (navegación entre vistas)
watch(() => route.path, async (newPath) => {
  if (newPath === '/charger-results-map') {
    // Recargar filtros desde localStorage al entrar en la vista del mapa
    const previousFilters = activeFilters.value ? JSON.stringify(activeFilters.value) : null;
    loadFiltersFromStorage();
    const currentFilters = activeFilters.value ? JSON.stringify(activeFilters.value) : null;

    // Si los filtros cambiaron, recargar resultados (reemplazo completo)
    // Pero NO en la primera carga inicial (para evitar llamadas duplicadas)
    if (previousFilters !== currentFilters && !isInitialLoad.value) {
      isIncrementalUpdate.value = false;
      await loadSearchResults();
    }
  }
});

// Hook para cuando el componente se activa (si está dentro de keep-alive)
onActivated(async () => {
  // Recargar filtros y resultados cuando se activa el componente
  loadFiltersFromStorage();
  
  // Solo recargar si NO es la primera carga (para evitar llamadas duplicadas)
  if (!isInitialLoad.value) {
    isIncrementalUpdate.value = false; // Reemplazo completo al activar
    await loadSearchResults();
  }

  // Si hay una ubicación guardada en searchData y NO es la ubicación por defecto (Madrid),
  // usarla para mostrar el marcador inmediatamente
  if (searchData.value?.location) {
    const savedLat = searchData.value.location.lat;
    const savedLon = searchData.value.location.lon;
    // Solo usar la ubicación guardada si no es la ubicación por defecto
    const isDefaultLocation = Math.abs(savedLat - latitudMadrid) < 0.001 &&
      Math.abs(savedLon - longitudMadrid) < 0.001;

    if (!isDefaultLocation) {
      userLocation.value = {
        lat: savedLat,
        lon: savedLon
      };
    }
  }

  // Activar automáticamente el botón de localización al entrar en la vista
  // Esperar a que el mapa esté disponible
  await nextTick();
  setTimeout(() => {
    const map = lmap.value?.leafletObject;
    if (map && !isLocateFollowing.value && !isLocating.value) {
      userClickedLocateButton.value = true;
      startLocationTracking();
    }
  }, 200);

});

onMounted(async () => {
  // Verificar si la geolocalización está disponible al inicio
  if (!navigator.geolocation) {
    const errorMessage = t("components.chargerresultsmapview.navegador_no_soporta_geolocalizacion");
    locationError.value = errorMessage;
  }

  // Aplicar los parches antes de cualquier operación del mapa
  patchLeafletEventHandling();
  patchLeafletIntersects();
  patchLeafletCircle();
  patchLeafletLayerAnimation();

  // Manejador global de errores para capturar errores de _latLngToNewLayerPoint en dispositivos móviles
  globalErrorHandler = (event: ErrorEvent) => {
    const errorMessage = event.message || event.error?.message || '';
    const errorStack = event.error?.stack || '';
    const errorDetails = event.error?.details || '';
    
    // Capturar específicamente el error "null is not an object (evaluating 'this._map._latLngToNewLayerPoint')"
    // Este error puede aparecer en diferentes formatos dependiendo del navegador
    const isLatLngError = 
      errorMessage.includes('_latLngToNewLayerPoint') ||
      errorMessage.includes('null is not an object') ||
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes('Cannot read property') ||
      errorMessage.includes('_map._latLngToNewLayerPoint') ||
      errorStack.includes('_latLngToNewLayerPoint') ||
      errorStack.includes('_animateZoom') ||
      errorDetails.includes('_latLngToNewLayerPoint') ||
      errorDetails.includes('null is not an object');
    
    if (isLatLngError) {
      // Silenciar el error - no hacer nada
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    
    // Permitir que otros errores se propaguen normalmente
    return true;
  };
  
  // Agregar el manejador de errores global
  window.addEventListener('error', globalErrorHandler, true);

  // Manejador global para promesas rechazadas relacionadas con _latLngToNewLayerPoint
  globalRejectionHandler = (event: PromiseRejectionEvent) => {
    const errorMessage = event.reason?.message || event.reason?.toString() || String(event.reason) || '';
    const errorStack = event.reason?.stack || '';
    const errorDetails = event.reason?.details || '';
    
    // Capturar específicamente el error "null is not an object (evaluating 'this._map._latLngToNewLayerPoint')"
    // Este error puede aparecer en diferentes formatos dependiendo del navegador
    const isLatLngError = 
      errorMessage.includes('_latLngToNewLayerPoint') ||
      errorMessage.includes('_map._latLngToNewLayerPoint') ||
      errorMessage.includes('null is not an object') ||
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes('Cannot read property') ||
      errorStack.includes('_latLngToNewLayerPoint') ||
      errorStack.includes('_animateZoom') ||
      errorDetails.includes('_latLngToNewLayerPoint') ||
      errorDetails.includes('null is not an object');
    
    if (isLatLngError) {
      // Silenciar el error - no hacer nada
      event.preventDefault();
      return;
    }
  };
  
  // Agregar el manejador de promesas rechazadas
  window.addEventListener('unhandledrejection', globalRejectionHandler);

  // Cargar filtros desde localStorage antes de cargar resultados
  loadFiltersFromStorage();

  // Aplicar límites del mapa después de que se inicialice
  await nextTick();

  // Esperar un poco más para asegurar que el mapa esté completamente inicializado
  setTimeout(() => {
    const map = lmap.value?.leafletObject;
    if (map) {
      map.setMaxBounds(spainBounds);

      // Detectar interacción del usuario con el mapa para desactivar el seguimiento automático
      map.on('dragstart', () => {
        stopLocationTracking();
      });
      
      map.on('zoomstart', () => {
        stopLocationTracking();
      });

      // Escuchar eventos del mapa para capturar errores de geolocalización
      map.on('locationerror', (error: any) => {
        console.warn('Error de geolocalización desde el mapa:', error);

        let errorMessage = t("components.chargerresultsmapview.no_pudo_acceder_ubicacion");

        if (error?.code !== undefined) {
          switch (error.code) {
            case 1: // PERMISSION_DENIED
              errorMessage = t("components.chargerresultsmapview.permiso_ubicacion_denegado");
              break;
            case 2: // POSITION_UNAVAILABLE
              errorMessage = t("components.chargerresultsmapview.ubicacion_no_disponible");
              break;
            case 3: // TIMEOUT
              errorMessage = t("components.chargerresultsmapview.tiempo_espera_agotado");
              break;
            default:
              errorMessage = t("components.chargerresultsmapview.no_pudo_acceder_ubicacion");
              break;
          }
        } else if (error?.message) {
          const errorMsg = error.message.toLowerCase();
          if (errorMsg.includes('permission') || errorMsg.includes('policy') || errorMsg.includes('geolocation')) {
            // Detectar violaciones de política de permisos
            errorMessage = t("components.chargerresultsmapview.geolocalizacion_bloqueada");
          }
        }

        // Mostrar el error tanto en el mensaje superior como en la ventana flotante
        // Esto asegura que el mensaje superior aparezca siempre que no haya acceso a la ubicación
        locationError.value = errorMessage;
        locationErrorFromButton.value = errorMessage;
      });

      // Agregar listeners para cuando el mapa se mueva
      map.on('moveend', () => {
        calculateDistanceToMapCenter();
      });

      map.on('dragend', () => {
        calculateDistanceToMapCenter();
      });

      map.on('zoomend', () => {
        calculateDistanceToMapCenter();
      });
      
      // Añadir listener para orientación del dispositivo (DeviceOrientationEvent)
      // Esto es especialmente útil en dispositivos móviles
      if (typeof DeviceOrientationEvent !== 'undefined' && typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        // iOS 13+ requiere permiso explícito
        (DeviceOrientationEvent as any).requestPermission()
          .then((response: string) => {
            if (response === 'granted') {
              window.addEventListener('deviceorientation', handleDeviceOrientation);
            }
          })
          .catch(() => {
            // Si falla el permiso, no hacer nada
          });
      } else {
        // Para navegadores que no requieren permiso
        window.addEventListener('deviceorientation', handleDeviceOrientation);
      }

      // Agregar listener de clic en el mapa para cerrar el panel cuando se hace clic en el mapa vacío
      // IMPORTANTE: Este listener debe ejecutarse ANTES que los listeners de los marcadores
      // Usamos el evento nativo del DOM en lugar del evento de Leaflet para tener más control
      const mapContainer = map.getContainer();
      mapContainer.addEventListener('click', (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        // Verificar si el clic fue en un marcador
        const isMarkerClick = target.closest('.leaflet-marker-icon, .charger-marker, .leaflet-marker-pane');
        if (isMarkerClick) {
          return;
        }

        // Verificar si el clic fue en el panel flotante
        const isPanelClick = target.closest('.floating-panel');
        if (isPanelClick) {
          return;
        }

        // Verificar si el clic fue en algún control de Leaflet (zoom, etc.)
        const isControlClick = target.closest('.leaflet-control');
        if (isControlClick) {
          return;
        }

        // Si llegamos aquí, el clic fue en el mapa vacío
        // Detener el seguimiento automático si está activo
        if (isLocateFollowing.value) {
          stopLocationTracking();
        }
        
        // Cerrar el panel si está abierto
        if (selectedPoint.value) {
          closeModal();
        }
      }, true); // Usar capture phase para que se ejecute antes que otros listeners

      // Calcular distancia inicial y establecer el centro anterior
      // Esto calcula el radio visible correcto antes de hacer la primera llamada
      calculateDistanceToMapCenter();
      // Establecer el centro inicial para comparaciones futuras
      if (!previousMapCenter.value && map.getCenter()) {
        const initialCenter = map.getCenter();
        previousMapCenter.value = {
          lat: initialCenter.lat,
          lng: initialCenter.lng
        };
      }


      // Hacer la primera carga DESPUÉS de que el mapa esté inicializado y se haya calculado el radio visible
      // Solo si es la primera carga (isInitialLoad.value === true)
      if (isInitialLoad.value) {
        isIncrementalUpdate.value = false; // Primera carga: reemplazo completo
        isInitialLoad.value = false; // Marcar que ya se hizo la primera carga
        loadSearchResults().catch(err => {
          console.error('Error en la primera carga de resultados:', err);
        });
      }

      // Activar automáticamente el botón de localización al entrar en la vista
      // Esperar un poco más para asegurar que todo esté completamente inicializado
      setTimeout(() => {
        if (!isLocateFollowing.value && !isLocating.value) {
          userClickedLocateButton.value = true;
          startLocationTracking();
        }
      }, 200);
    } else {
      console.warn('No se pudo obtener el objeto del mapa');
    }
  }, 100);

  // Si hay una ubicación guardada en searchData y NO es la ubicación por defecto (Madrid),
  // usarla para mostrar el marcador inmediatamente
  if (searchData.value?.location) {
    const savedLat = searchData.value.location.lat;
    const savedLon = searchData.value.location.lon;
    // Solo usar la ubicación guardada si no es la ubicación por defecto
    const isDefaultLocation = Math.abs(savedLat - latitudMadrid) < 0.001 &&
      Math.abs(savedLon - longitudMadrid) < 0.001;

    if (!isDefaultLocation) {
      userLocation.value = {
        lat: savedLat,
        lon: savedLon
      };
    }
  }

});

// Función para manejar la orientación del dispositivo
function handleDeviceOrientation(event: DeviceOrientationEvent) {
  let newHeading: number | null = null;
  
  // iOS usa webkitCompassHeading en lugar de alpha
  // webkitCompassHeading es el heading de la brújula (0-360) donde 0 = norte, 270° = oeste
  if ((event as any).webkitCompassHeading !== undefined && (event as any).webkitCompassHeading !== null) {
    const compassHeading = (event as any).webkitCompassHeading;
    if (!isNaN(compassHeading)) {
      newHeading = compassHeading;
      // Guardar el valor de webkitCompassHeading para depuración
      deviceAlpha.value = compassHeading;
      // Marcar que estamos usando iOS
      isIOS.value = true;
    }
  }
  // Android y otros dispositivos usan alpha
  // En Android: alpha tiene el sistema invertido (90° = oeste en lugar de 270°)
  else if (event.alpha !== null && !isNaN(event.alpha)) {
    // Guardar el valor de alpha para depuración
    deviceAlpha.value = event.alpha;
    // alpha es la orientación en grados (0-360) pero con sistema invertido
    newHeading = event.alpha;
    // Marcar que estamos usando Android
    isIOS.value = false;
  }
  
  // Si tenemos un heading válido, actualizar
  if (newHeading !== null) {
    // Solo actualizar si el heading cambió significativamente (más de 1 grado)
    // Esto evita actualizaciones innecesarias y mejora el rendimiento
    if (userHeading.value === null || Math.abs(newHeading - userHeading.value) > 1) {
      userHeading.value = newHeading;
      
      // Actualizar el icono del marcador si existe
      if (userLocationMarker.value && userLocation.value) {
        const newIcon = L.divIcon({
          className: 'user-location-marker',
          html: createLocationIconHtml(userHeading.value),
          iconSize: [40, 40],
          iconAnchor: [20, 20]
        });
        userLocationMarker.value.setIcon(newIcon);
      }
    }
  }
}

// Limpiar listeners al desmontar el componente
onBeforeUnmount(() => {
  // Remover listener de deviceorientation
  window.removeEventListener('deviceorientation', handleDeviceOrientation);
  
  // Remover el manejador global de errores
  if (globalErrorHandler) {
    window.removeEventListener('error', globalErrorHandler, true);
    globalErrorHandler = null;
  }
  
  // Remover el manejador de promesas rechazadas
  if (globalRejectionHandler) {
    window.removeEventListener('unhandledrejection', globalRejectionHandler);
    globalRejectionHandler = null;
  }
  
  // Detener el seguimiento de ubicación si está activo
  stopLocationTracking();
  
  // Remover el marcador y círculo de ubicación del usuario
  removeUserLocationMarker();
});
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";

.page {
  display: grid;
  height: 100dvh;
  grid-template-rows: 1fr auto;
  background: var(--background-default-page);
}

:deep(.leaflet-touch .leaflet-control-zoom-in),
:deep(.leaflet-touch .leaflet-control-zoom-out) {
  color: var(--text-default-caption, #667790);
  font-size: 36px;
  width: 36px;
  height: 36px;
  background: var(--background-default-default);
  border-radius: var(--border-radius-sm, 4px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.map-container {
  position: relative;
  flex: 1;
  width: 100%;
}

.map-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.title-bar-button {
  background: var(--background-default-default);
  border: 0;
  font-size: 24px;
  padding: var(--spacing-xs, 8px);
  border-radius: var(--border-radius-sm, 4px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  i {
    color: var(--text-default-caption, #667790);
  }
}

.map-filters-button {
  position: absolute;
  top: var(--spacing-sm, 8px);
  right: var(--spacing-sm, 8px);
  z-index: 1000;
  border: 0;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
  width: 40px;
  height: 40px;

  .filter-indicator {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 12px;
    height: 12px;
    background-color: #1863ED;
    border-radius: 50%;
    border: 2px solid var(--background-default-default, #FFF);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
}

.map-clear-markers-button {
  position: absolute;
  top: calc(var(--spacing-sm, 8px) + 80px + var(--spacing-xs, 8px) * 2);
  right: var(--spacing-sm, 8px);
  z-index: 1000;
  border: 0;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
  width: 40px;
  height: 40px;
}

.map-location-button {
  position: absolute;
  top: calc(var(--spacing-sm, 8px) + 36px + 2px + 36px + 2px);
  left: var(--spacing-sm, 8px);
  z-index: 1000;
  border: 0;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
  width: 36px;
  height: 36px;
  padding: 0;
  font-size: 20px;
  transition: all 0.2s;

  i {
    font-size: 20px;
  }

  &.active {
    background: var(--background-default-default);
    
    i {
      color: var(--background-primary-default, #D3135A);
    }
  }

  &.locating {
    i {
      animation: spin 1s linear infinite;
    }
  }

  &.error {
    i {
      color: var(--text-error-default, #cb1212);
    }
  }

  &:hover {
    transform: scale(1.05);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

:deep(.leaflet-container) {
  width: 100%;
  height: 100%;
  font-family: var(--font-family-body, "Gantari");
}

:deep(h3.title) {
  text-align: left;
  padding-right: 30px;
}

:deep(.card) {
  border: 0;
}

.floating-panel {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 12px;
  max-width: 400px;
  width: calc(100% - 40px);
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateX(-50%) translateY(100%);
    opacity: 0;
  }

  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

.close-button {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: #6b7280;
  z-index: 10;
  transition: all 0.2s;
}

.close-button:hover {
  background: #e5e7eb;
  color: #374151;
}

// Marcador de cargador
:deep(.charger-marker) {
  background: transparent;
  border: none;
}

// Mensaje de error de ubicación (mensaje superior original)
.message-container {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  display: flex;
  padding: var(--card-vertical-padding, 16px) var(--card-horizontal-padding, 16px);
  align-items: center;
  gap: 10px;
  max-width: 90%;
  border-radius: var(--card-border-radius, 16px);
  background: var(--background-normal-hover, #F0F0F0);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  .location-icon {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
  }

  .message-text {
    color: var(--text-default-caption, #667790);
    font-size: var(--font-size-body-sm, 14px);
    font-weight: 400;
    line-height: var(--line-height-body-sm, 18px);
    flex: 1;

    .help-link {
      color: var(--text-primary-default, #d3135a);
      text-decoration: underline;
      cursor: pointer;
      margin-left: 4px;

      &:hover {
        color: var(--text-primary-hover, #960d40);
      }
    }
  }
}

// Ventana flotante de error de geolocalización (similar a floating-panel)
.floating-error-panel {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--background-default-default, #FFF);
  border-radius: var(--card-border-radius, 16px);
  border: 1px solid var(--border-default-default, #C2CDDD);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  width: calc(100% - 40px);
  z-index: 1000;
  animation: slideUp 0.3s ease-out;
  padding: var(--spacing-md) var(--card-horizontal-padding, 16px);
}

.error-content {
  display: flex;
  gap: var(--spacing-md, 16px);
  align-items: flex-start;
}

.error-icon {
  flex-shrink: 0;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-error-default, #cb1212);
  font-size: 24px;
}

.error-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs, 8px);
}

.error-title {
  color: var(--text-primary-default, #D3135A);
  font-size: var(--font-size-body-lg, 18px);
  font-weight: 700;
  line-height: var(--line-height-body-md, 24px);
}

.error-message {
  color: var(--text-default-default, #000);
  font-size: var(--font-size-body-sm, 14px);
  font-weight: 400;
  line-height: var(--line-height-body-md, 24px);
}

.help-link {
  color: var(--text-primary-default, #d3135a);
  text-decoration: underline;
  cursor: pointer;
  font-size: var(--font-size-body-sm, 14px);
  margin-top: var(--spacing-xs, 8px);

  &:hover {
    color: var(--text-primary-hover, #960d40);
  }
}

</style>
